import {
  ActionFunction,
  LoaderFunction,
  Navigate,
  Outlet,
  useMatch,
  useNavigate,
  useNavigation,
  useParams,
} from 'react-router-dom';
import { ResolverSidebar } from '../resolver/Sidebar';
import {
  useTask,
  useTasks,
  useResolverUrl,
  useTaskUrl,
  useNextTask,
  useTaskActionData,
  useIsNoticesAndPostersTask,
} from '../utils/hooks';
import { api } from '../../../utils/fetchApi';
import { ResolverLocationHeader } from '../common/LocationHeader';
import { TaskRouteParams, TaskStatus, TaskType } from '../utils/types';
import { TaskQuestion } from './TaskQuestion';
import { TaskRequirement } from './TaskRequirement';
import { TaskSummary } from './TaskSummary';
import { useEffect, useState } from 'react';
import { Loading } from '../../Loading';
import { TaskActionResponse } from './types';
import { markTaskAsCompleteForSession } from '../utils/session';

export const action: ActionFunction = async ({
  params,
  request,
}): Promise<TaskActionResponse> => {
  const { taskId } = params as TaskRouteParams;
  const { intent, ...data } = Object.fromEntries(await request.formData());
  let response;
  let newTasks = [];

  // TODO: Better errors!!!

  if (intent === 'mark-as-done') {
    try {
      response = await api({
        url: `/api/compliance/tasks/${taskId}/status`,
        method: request.method,
        body: {
          status: TaskStatus.Done,
        },
      });
    } catch (error) {
      return {
        errors: {
          submit: 'Something went wrong, please try again.',
        },
        resolved: false,
        newTasks: [],
      };
    }

    newTasks = await response.json();
  } else if (intent === 'answer-question') {
    const answers = Object.values(data);

    if (answers.length === 0) {
      return {
        errors: {
          submit: 'Please answer the question to proceed.',
        },
        resolved: false,
        newTasks: [],
      };
    }

    const [answer] = answers;

    try {
      response = await api({
        url: `/api/compliance/tasks/${taskId}/answer`,
        method: request.method,
        body: {
          answer,
        },
      });
    } catch (error) {
      return {
        errors: {
          submit: 'Something went wrong, please try again.',
        },
        resolved: false,
        newTasks: [],
      };
    }

    newTasks = await response.json();
  } else if (intent === 'resolve-notices') {
    try {
      await api({
        url: `/api/notices/enrollment/${data.locationId}/${taskId}`,
        method: 'POST',
      });
    } catch (error) {
      return {
        errors: {
          submit: 'Something went wrong, please try again.',
        },
        resolved: false,
        newTasks: [],
      };
    }
  }

  markTaskAsCompleteForSession(taskId);

  return {
    errors: {},
    resolved: true,
    newTasks,
  };
};

export const loader: LoaderFunction = async ({ params: { taskId } }) => {
  return api({
    url: `/api/compliance/tasks/${taskId}`,
    method: 'GET',
  });
};

export const Component = () => {
  const { resolverType } = useParams();
  const tasks = useTasks();
  const {
    id,
    source: { type },
  } = useTask();
  const actionData = useTaskActionData();
  const resolverUrl = useResolverUrl();
  const taskUrl = useTaskUrl();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const nextTask = useNextTask();
  const isNotices = useIsNoticesAndPostersTask();
  const [showLoader, setShowLoader] = useState(false);
  const isAutomationForm = useMatch(`${taskUrl}/automation`);

  useEffect(() => {
    if (actionData?.resolved) {
      if (navigation.state === 'idle') {
        const newTasks = actionData.newTasks;
        const newTasksInTaskList = tasks.filter((_task) => {
          return newTasks.find((newTask) => newTask.id === _task.id);
        });

        if (
          (newTasksInTaskList.length === 0 && !isNotices) ||
          resolverType === 'assessment'
        ) {
          navigate(
            `${resolverUrl}/${nextTask ? `task/${nextTask.id}` : 'complete'}`
          );
        } else {
          setShowLoader(false);
        }
      } else {
        setShowLoader(true);
      }
    }
  }, [
    actionData,
    tasks,
    nextTask,
    navigate,
    navigation.state,
    isNotices,
    resolverUrl,
    resolverType,
  ]);

  // TODO: This needs to be more robust
  if (tasks.length && !tasks.find((_task) => _task.id === id)) {
    return <Navigate to={resolverUrl} replace />;
  }

  let content = null;

  if (actionData?.resolved) {
    if (showLoader) {
      content = (
        <div className="w-full">
          <Loading />
        </div>
      );
    } else {
      content = <TaskSummary />;
    }
  } else if (type === TaskType.Question) {
    content = <TaskQuestion />;
  } else if (!isAutomationForm) {
    content = <TaskRequirement />;
  }

  return (
    <section>
      <ResolverLocationHeader />

      <div className="mx-auto h-[calc(100vh-64px-80px)] max-w-screen-2xl p-6 @container/task">
        <div className="flex h-full border bg-white">
          {tasks.length > 1 && (
            <aside className="w-72 shrink-0 overflow-y-auto border-r bg-teal-200 @6xl/task:w-96">
              <ResolverSidebar />
            </aside>
          )}

          {content}
          <Outlet />
        </div>
      </div>
    </section>
  );
};
